/**
 * usePublishedSwimlane - Data fetching for published swimlane views
 *
 * Backend Contract:
 * GET /published/v1/roadmap/visualization/:linkId - Fetch published visualization data
 *
 * The backend resolves the URL token and returns all data needed to render the view,
 * including metadata, preferences, vision statement, legend items, and swimlane data.
 *
 * Password Protection:
 * - 401: Password required (no password or missing header)
 * - 403: Invalid password
 * - Password sent via X-Published-View-Password header
 */

import { useEffect, useState } from 'react';
import { usePublishedViewAuth } from './PublishedViewAuthContext';
import type {
  FetchResult,
  HttpErrorStatus,
  PublishedSwimlaneResponse,
  UsePublishedSwimlaneResult,
} from './types';
import { asHttpErrorStatus } from './types';

// ============================================================================
// Constants
// ============================================================================

/**
 * HTTP status codes for password protection and not found scenarios.
 * Branded as HttpErrorStatus to ensure type safety in switch statements.
 */
const HTTP_BAD_REQUEST = 400 as HttpErrorStatus;
const HTTP_UNAUTHORIZED = 401 as HttpErrorStatus;
const HTTP_FORBIDDEN = 403 as HttpErrorStatus;
const HTTP_NOT_FOUND = 404 as HttpErrorStatus;

// ============================================================================
// Pure Functions
// ============================================================================

/**
 * Fetch JSON with type-honest result - no fake status codes for non-HTTP errors
 */
async function fetchJson<T>(
  url: string,
  label: string,
  headers?: HeadersInit,
): Promise<FetchResult<T>> {
  try {
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const status = asHttpErrorStatus(response.status);
      if (status === null) {
        // Defensive: unexpected status code outside 4xx-5xx range
        return {
          type: 'failure',
          error: new Error(`Unexpected HTTP status ${response.status} fetching ${label}`),
        };
      }
      return {
        type: 'http_error',
        status,
        error: new Error(`Failed to fetch ${label}: ${response.status} ${response.statusText}`),
      };
    }
    const data = await response.json();
    return { type: 'success', data };
  } catch (e) {
    // Network error, JSON parse error, etc. - no HTTP status exists
    return {
      type: 'failure',
      error: e instanceof Error ? e : new Error(`Network error fetching ${label}`),
    };
  }
}

// ============================================================================
// Data Fetching Pipeline
// ============================================================================

/**
 * Fetch published swimlane data from visualization backend
 */
async function fetchPublishedSwimlaneData(
  linkId: string,
  password?: string,
): Promise<FetchResult<PublishedSwimlaneResponse>> {
  const headers: HeadersInit = {};
  if (password) {
    headers['X-Published-View-Password'] = password;
  }

  const result = await fetchJson<PublishedSwimlaneResponse>(
    `/published/v1/roadmap/visualization/${linkId}`,
    'published visualization',
    headers,
  );

  if (result.type !== 'success') {
    return result;
  }

  // Validate required fields - this is a data failure, not an HTTP error
  if (!result.data.layout) {
    return {
      type: 'failure',
      error: new Error('Layout configuration is required in published swimlane response'),
    };
  }

  return result;
}

// ============================================================================
// Pure Transformations
// ============================================================================

/**
 * Transform fetch result to hook result - pure function, no side effects
 * Maps HTTP semantics to domain states exhaustively
 */
function toHookResult(
  fetchResult: FetchResult<PublishedSwimlaneResponse>,
): UsePublishedSwimlaneResult {
  switch (fetchResult.type) {
    case 'success':
      return { status: 'success', data: fetchResult.data };

    case 'http_error':
      // Map HTTP status to domain state
      if (fetchResult.status === HTTP_UNAUTHORIZED) {
        return { status: 'password_required' };
      }
      if (fetchResult.status === HTTP_FORBIDDEN) {
        return { status: 'password_invalid' };
      }
      if (fetchResult.status === HTTP_BAD_REQUEST || fetchResult.status === HTTP_NOT_FOUND) {
        // Both invalid URL format (400) and not found (404) show same user-friendly message
        return { status: 'not_found' };
      }
      return { status: 'error', error: fetchResult.error };

    case 'failure':
      // Non-HTTP failure (network error, validation error, etc.)
      return { status: 'error', error: fetchResult.error };
  }
}

// ============================================================================
// React Hook
// ============================================================================

/**
 * Hook for fetching published swimlane data
 *
 * Consumes password submission from PublishedViewAuthContext.
 * Using PasswordSubmission (with submittedAt) instead of plain string
 * ensures React state changes on retry with same password.
 *
 * @param linkId - The published view URL token
 * @returns Discriminated union result with status
 */
export function usePublishedSwimlane(linkId: string): UsePublishedSwimlaneResult {
  const { submission } = usePublishedViewAuth();
  const [result, setResult] = useState<UsePublishedSwimlaneResult>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    setResult({ status: 'loading' });

    fetchPublishedSwimlaneData(linkId, submission?.password).then((fetchResult) => {
      if (!cancelled) {
        setResult(toHookResult(fetchResult));
      }
    });

    return () => {
      cancelled = true;
    };
    // Include submission object - submittedAt changes force re-fetch even with same password
  }, [linkId, submission]);

  return result;
}
